import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Ruler, Clock, Calendar, Target, Lightbulb, TrendingUp, CheckCircle2, Film } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import VideoEmbed from "@/components/public/VideoEmbed";
import CallbackForm from "@/components/public/CallbackForm";
import api, { fileUrl } from "@/lib/api";
import Seo from "@/components/Seo";

const FALLBACK = "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1400&q=80";

export default function RealisationDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/realisations/${slug}`).then((res) => setR(res.data)).catch(() => {}); }, [slug]);
  if (!r) return <div className="pt-40 pb-40 text-center text-stone">Chargement…</div>;

  const ld = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: r.title,
    about: r.category,
    locationCreated: r.location,
    description: r.summary || r.seo_description,
    image: r.cover_image ? fileUrl(r.cover_image) : undefined,
  };

  const meta = [
    r.location && { icon: MapPin, label: "Lieu", value: r.location },
    r.surface && { icon: Ruler, label: "Surface", value: r.surface },
    r.duration && { icon: Clock, label: "Durée", value: r.duration },
    r.year && { icon: Calendar, label: "Année", value: r.year },
  ].filter(Boolean);

  return (
    <div>
      <Seo title={r.seo_title || r.title} path={`/realisations/${r.slug}`} description={r.seo_description || r.summary} image={r.cover_image ? fileUrl(r.cover_image) : undefined} type="article" jsonLd={ld} />
      <section className="relative h-[60vh] min-h-[420px] flex items-end" data-testid="realisation-hero">
        <img src={fileUrl(r.cover_image) || FALLBACK} alt={r.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-12">
          <Link to="/realisations" className="inline-flex items-center gap-2 text-cream/70 hover:text-cream text-sm mb-4"><ArrowLeft size={16} /> Toutes les réalisations</Link>
          <span className="text-xs font-bold uppercase tracking-wider text-terracotta">{r.category}</span>
          <h1 className="font-display text-5xl lg:text-6xl text-cream mt-2">{r.title}</h1>
          {r.location && <p className="text-cream/70 flex items-center gap-1 mt-2"><MapPin size={15} />{r.location}</p>}
        </div>
      </section>

      {meta.length > 0 && (
        <div className="bg-forest border-b border-cream/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {meta.map((m) => (
              <div key={m.label} className="flex items-center gap-3">
                <m.icon className="text-terracotta" size={22} />
                <div><p className="text-[11px] uppercase tracking-wider text-cream/50">{m.label}</p><p className="text-cream font-medium">{m.value}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
        {/* Left : gallery */}
        <div className="lg:sticky lg:top-28 space-y-4" data-testid="realisation-gallery">
          {(r.gallery?.length > 0 ? r.gallery : [r.cover_image]).filter(Boolean).map((g, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08}>
              <img src={fileUrl(g)} alt={`${r.title} ${i + 1}`} className="rounded-2xl w-full aspect-[4/3] object-cover shadow-sm" />
            </Reveal>
          ))}
        </div>

        {/* Right : texte */}
        <div>
          {r.summary && <Reveal><p className="text-xl text-forest leading-relaxed font-display">{r.summary}</p></Reveal>}

          {[{ icon: Target, title: "Le défi", body: r.challenge }, { icon: Lightbulb, title: "Notre solution", body: r.solution }, { icon: TrendingUp, title: "Les résultats", body: r.results }]
            .filter((b) => b.body).map((b, i) => (
            <Reveal key={i} delay={0.05} className="mt-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center"><b.icon className="text-brand" size={20} /></div>
                <h2 className="font-display text-3xl text-forest">{b.title}</h2>
              </div>
              <p className="text-stone leading-relaxed whitespace-pre-line">{b.body}</p>
            </Reveal>
          ))}

          {r.services_used?.length > 0 && (
            <Reveal className="mt-12">
              <Overline>Prestations mobilisées</Overline>
              <div className="mt-3 flex flex-wrap gap-2">
                {r.services_used.map((s) => <span key={s} className="rounded-full bg-sage px-4 py-1.5 text-sm text-forest font-medium">{s}</span>)}
              </div>
            </Reveal>
          )}

          {r.tasks?.filter(Boolean).length > 0 && (
            <Reveal className="mt-12" data-testid="realisation-tasks">
              <Overline>Travaux réalisés sur ce chantier</Overline>
              <ul className="mt-4 space-y-2.5">
                {r.tasks.filter(Boolean).map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone"><CheckCircle2 size={18} className="text-brand mt-0.5 shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
      </section>

      {/* Interviews vidéo */}
      {r.interviews?.filter((v) => v?.url).length > 0 && (
        <section className="bg-forest py-20" data-testid="realisation-interviews">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <Reveal>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-flora">Paroles de chantier</span>
              <h2 className="mt-3 font-display text-4xl text-cream">Ils parlent du projet</h2>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-2 gap-8">
              {r.interviews.filter((v) => v?.url).map((v, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <VideoEmbed url={v.url} title={v.name} />
                  <div className="mt-3">
                    {v.name && <p className="text-cream font-semibold">{v.name}</p>}
                    {v.role && <p className="text-cream/60 text-sm">{v.role}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Galerie vidéo du projet */}
      {r.video_gallery?.filter(Boolean).length > 0 && (
        <section className="py-20 bg-cream" data-testid="realisation-videos">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <Reveal className="flex items-center gap-3 mb-10">
              <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center"><Film className="text-brand" size={20} /></div>
              <h2 className="font-display text-4xl text-forest">Le chantier en vidéo</h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-6">
              {r.video_gallery.filter(Boolean).map((u, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}><VideoEmbed url={u} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F0EFEA] py-20">
        <div className="max-w-2xl mx-auto px-6">
          <div className="rounded-2xl bg-white border border-border p-8 shadow-sm">
            <h3 className="font-display text-3xl text-forest mb-1 text-center">Un projet similaire ?</h3>
            <p className="text-stone text-center mb-6">Parlons-en, l'étude est gratuite.</p>
            <CallbackForm compact />
          </div>
        </div>
      </section>
    </div>
  );
}
